import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config.js';

const getSearchParamFilterValues = (searchParams, column) => {
  const arrayValues = searchParams.getAll(`${column}[]`);
  if (arrayValues.length > 0)
    return arrayValues;

  const singleValue = searchParams.get(column);
  return singleValue ? [singleValue] : [];
};

/**
 * Encapsulates the "project jail" feature where the gallery is locked
 * to a fixed subset of projects passed via URL params (?jail=true&project[]=...).
 *
 * @param {URLSearchParams} searchParams - initial URL search params (read only at mount).
 * @param {Function} fetchWithAuth - authenticated fetch helper.
 */
export function useProjectJail({ searchParams, fetchWithAuth }) {
  const jailRef = useRef(searchParams.get('jail') === 'true');
  const jailedProjectValuesRef = useRef(
    jailRef.current ? getSearchParamFilterValues(searchParams, 'project') : []
  );

  const jailedProjectValues = jailedProjectValuesRef.current;
  const isProjectJailActive = jailRef.current && jailedProjectValues.length > 0;

  const [jailedProjectOptions, setJailedProjectOptions] = useState([]);
  const [jailedProjectLabelsLoading, setJailedProjectLabelsLoading] = useState(isProjectJailActive);
  const labelsFetchedRef = useRef(false);

  useEffect(() => {
    if (!isProjectJailActive || labelsFetchedRef.current)
      return;

    let cancelled = false;

    const fetchJailedProjectLabels = async () => {
      try {
        const params = new URLSearchParams();
        jailedProjectValues.forEach(value => {
          params.append('project[]', value);
        });

        const response = await fetchWithAuth(`${API_BASE_URL}/api/columns/project/labels?${params}`);
        const values = await response.json();

        if (cancelled)
          return;

        const jailedSet = new Set(jailedProjectValues.map(value => `${value}`));
        const labelsByValue = new Map(
          values
            .filter(option => jailedSet.has(`${option.value}`))
            .map(option => [`${option.value}`, option])
        );

        setJailedProjectOptions(
          jailedProjectValues.map(value => labelsByValue.get(`${value}`) || { value, label: value })
        );
        labelsFetchedRef.current = true;
      } catch (error) {
        console.error('Error fetching jailed project labels:', error);
      } finally {
        if (!cancelled)
          setJailedProjectLabelsLoading(false);
      }
    };

    fetchJailedProjectLabels();

    return () => {
      cancelled = true;
    };
  }, [fetchWithAuth, isProjectJailActive, jailedProjectValues]);

  const sanitizeSelection = useCallback((value) => {
    if (!isProjectJailActive)
      return value;

    const jailedSet = new Set(jailedProjectValues.map(projectValue => `${projectValue}`));
    const selectedValues = Array.isArray(value)
      ? value
      : value ? [value] : [];

    return selectedValues.filter(selectedValue => jailedSet.has(`${selectedValue?.value}`));
  }, [isProjectJailActive, jailedProjectValues]);

  const getResetProjectsFilter = useCallback(() => {
    if (!isProjectJailActive)
      return null;

    return jailedProjectOptions.length > 0
      ? jailedProjectOptions
      : jailedProjectValues.map(value => ({ value, label: value }));
  }, [isProjectJailActive, jailedProjectOptions, jailedProjectValues]);

  const applyToSearchParams = useCallback((params) => {
    if (!isProjectJailActive)
      return;

    params.set('jail', 'true');

    if (!params.has('project') && !params.has('project[]')) {
      jailedProjectValues.forEach(value => {
        params.append('project[]', value);
      });
    }
  }, [isProjectJailActive, jailedProjectValues]);

  return {
    isProjectJailActive,
    jailedProjectValues,
    jailedProjectOptions,
    jailedProjectLabelsLoading,
    sanitizeSelection,
    getResetProjectsFilter,
    applyToSearchParams,
  };
}
